import { supabase } from '../lib/supabase';

/** Karşılaştırma için site URL’sini tek biçime getirir (host + path) */
export function normalizeComparableSiteUrl(raw: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    let path = parsed.pathname.replace(/\/+/g, '/').replace(/\/$/, '') || '';
    if (path === '/') path = '';
    const search = parsed.search;
    return `${host}${path}${search}`;
  } catch {
    return null;
  }
}

export type DuplicateSiteUrlMatch = {
  kind: 'forum' | 'suggestion';
  name: string;
  url: string;
  forumId?: string;
  suggestionStatus?: string | null;
};

function hostSearchToken(comparable: string): string | null {
  const host = comparable.split('/')[0]?.split('?')[0];
  return host && host.includes('.') ? host : null;
}

export function formatDuplicateSiteUrlMessage(match: DuplicateSiteUrlMatch): string {
  if (match.kind === 'forum') {
    return `Bu site zaten listede ekli: «${match.name}» — ${match.url}`;
  }
  if (match.suggestionStatus === 'pending') {
    return `Bu adres için zaten bekleyen bir öneri var: «${match.name}» — ${match.url}`;
  }
  return `Bu site daha önce önerilmiş: «${match.name}» — ${match.url}`;
}

/** forums + forum_suggestions içinde aynı site URL’si var mı */
export async function findDuplicateSiteUrl(
  url: string
): Promise<DuplicateSiteUrlMatch | null> {
  const comparable = normalizeComparableSiteUrl(url);
  if (!comparable) return null;

  const host = hostSearchToken(comparable);
  if (!host) return null;

  const { data: forums, error: forumsError } = await supabase
    .from('forums')
    .select('name, url, forum_id')
    .not('url', 'is', null)
    .ilike('url', `%${host}%`)
    .limit(80);

  if (forumsError) {
    console.warn('findDuplicateSiteUrl forums:', forumsError);
  } else {
    for (const row of forums ?? []) {
      if (row.url && normalizeComparableSiteUrl(row.url) === comparable) {
        return {
          kind: 'forum',
          name: row.name,
          url: row.url,
          forumId: row.forum_id,
        };
      }
    }
  }

  const { data: suggestions, error: suggestionsError } = await supabase
    .from('forum_suggestions')
    .select('name, url, status')
    .not('url', 'is', null)
    .ilike('url', `%${host}%`)
    .limit(80);

  if (suggestionsError) {
    console.warn('findDuplicateSiteUrl suggestions:', suggestionsError);
  } else {
    for (const row of suggestions ?? []) {
      if (row.url && normalizeComparableSiteUrl(row.url) === comparable) {
        return {
          kind: 'suggestion',
          name: row.name,
          url: row.url,
          suggestionStatus: row.status,
        };
      }
    }
  }

  return null;
}
